import React, { useRef, useEffect, useState } from 'react';
import { blockUpdates, allowUpdates } from '../utils/updateGate';
import '../styles/SignatureCanvas.css';

interface SignatureCanvasProps {
  onSignatureChange: (signatureData: string) => void;
  width?: number;
  height?: number;
  clearSignature?: boolean;
  mobileOptimized?: boolean;
}

const SignatureCanvas: React.FC<SignatureCanvasProps> = ({ 
  onSignatureChange, 
  width = 400, 
  height = 150,
  clearSignature = false,
  mobileOptimized = true
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [ctx, setCtx] = useState<CanvasRenderingContext2D | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  // A drawn signature exists nowhere but this canvas until the timesheet is
  // submitted, so a service-worker reload wipes it and the employee signs
  // again with a fingertip. Hold the gate for exactly as long as there is
  // something to lose — not for the component's whole lifetime, which would
  // block updates the entire time the timesheet is merely open.
  useEffect(() => {
    if (!hasSignature) return;
    blockUpdates();
    return () => { allowUpdates(); };
  }, [hasSignature]);

  // Determine actual dimensions based on expanded state and mobile optimization
  const actualWidth = mobileOptimized && !isExpanded ? Math.min(width, 200) : width;
  const actualHeight = mobileOptimized && !isExpanded ? Math.min(height, 80) : height;
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const context = canvas.getContext('2d');
    if (!context) return;
    
    // Set up the canvas
    context.lineWidth = 2;
    context.lineCap = 'round';
    context.strokeStyle = '#000000';
    setCtx(context);
    
    // Clear canvas on initial render
    context.clearRect(0, 0, canvas.width, canvas.height);
  }, []);
  
  // Handle canvas click to expand when in collapsed state
  const handleCanvasClick = () => {
    if (mobileOptimized && !isDrawing && !isExpanded) {
      setIsExpanded(true);
    }
  };
  
  useEffect(() => {
    if (clearSignature && ctx && canvasRef.current) {
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      onSignatureChange('');
      setHasSignature(false);
    }
  }, [clearSignature, ctx, onSignatureChange]);
  
  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!ctx) return;
    setIsDrawing(true);
    
    // Get the correct coordinates
    const coordinates = getCoordinates(e);
    if (!coordinates) return;
    
    ctx.beginPath();
    ctx.moveTo(coordinates.x, coordinates.y);
  };
  
  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !ctx) return;
    
    // Prevent scrolling when drawing on touch devices
    e.preventDefault();
    
    // Get the correct coordinates
    const coordinates = getCoordinates(e);
    if (!coordinates) return;
    
    ctx.lineTo(coordinates.x, coordinates.y);
    ctx.stroke();
  };
  
  const stopDrawing = () => {
    if (!isDrawing || !ctx || !canvasRef.current) return;
    
    setIsDrawing(false);
    ctx.closePath();
    
    // Convert canvas to base64 and pass to parent
    const signatureData = canvasRef.current.toDataURL('image/png');
    onSignatureChange(signatureData);
    setHasSignature(true);
  };
  
  const getCoordinates = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current) return null;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    let clientX: number;
    let clientY: number;
    
    // Handle both mouse and touch events
    if ('touches' in e) {
      // Touch event
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      // Mouse event
      clientX = e.clientX;
      clientY = e.clientY;
    }
    
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };
  
  const clearCanvas = () => {
    if (!ctx || !canvasRef.current) return;
    
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    onSignatureChange('');
    setHasSignature(false);
  };
  
  return (
    <div className={`signature-container ${isExpanded ? 'expanded' : ''}`}>
      {mobileOptimized && !isExpanded && (
        <div className="tap-to-sign-indicator">Tap to sign</div>
      )}
      <canvas
        ref={canvasRef}
        width={actualWidth}
        height={actualHeight}
        className={`signature-canvas ${isExpanded ? 'expanded' : ''}`}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
        onClick={handleCanvasClick}
      />
      <div className="signature-controls">
        <button 
          type="button" 
          className="clear-signature-button"
          onClick={clearCanvas}
        >
          Clear Signature
        </button>
        {mobileOptimized && isExpanded && (
          <button 
            type="button" 
            className="done-signature-button"
            onClick={() => setIsExpanded(false)}
          >
            Done
          </button>
        )}
      </div>
    </div>
  );
};

export default SignatureCanvas;
